import { z } from 'zod';
import {
  UlidSchema,
  TenantIdSchema,
  MontoClpSchema,
  IsoTimestampSchema,
  paginatedResponse,
} from './common.js';

/**
 * Mercado Vecinal — schemas para el marketplace cross-condominio.
 * A diferencia del resto del dominio, el marketplace NO esta scoped a un
 * solo tenant: cualquier residente autenticado puede ver y comprar articulos
 * de cualquier condominio.
 */

/* ------------------------------------------------------------------ */
/*  Enums                                                              */
/* ------------------------------------------------------------------ */

export const CategoriaListingSchema = z.enum([
  'muebles',
  'electronica',
  'electrodomesticos',
  'ropa',
  'deportes',
  'hogar',
  'libros',
  'juguetes',
  'vehiculos',
  'otros',
]);
export type CategoriaListing = z.infer<typeof CategoriaListingSchema>;

export const CondicionListingSchema = z.enum([
  'nuevo',
  'como_nuevo',
  'buen_estado',
  'usado',
]);
export type CondicionListing = z.infer<typeof CondicionListingSchema>;

export const EstadoListingSchema = z.enum([
  'draft',
  'active',
  'sold',
  'removed',
]);
export type EstadoListing = z.infer<typeof EstadoListingSchema>;

export const ModerationStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
]);
export type ModerationStatus = z.infer<typeof ModerationStatusSchema>;

export const RatingValueSchema = z.enum(['positive', 'neutral', 'negative']);
export type RatingValue = z.infer<typeof RatingValueSchema>;

export const ChatThreadStatusSchema = z.enum(['active', 'closed']);
export type ChatThreadStatus = z.infer<typeof ChatThreadStatusSchema>;

/* ------------------------------------------------------------------ */
/*  Listing                                                            */
/* ------------------------------------------------------------------ */

export const ListingPhotoSchema = z.object({
  s3Key: z.string(),
  url: z.string(),
  order: z.number().int().nonnegative(),
  moderationStatus: ModerationStatusSchema,
});
export type ListingPhoto = z.infer<typeof ListingPhotoSchema>;

export const ListingSchema = z.object({
  id: UlidSchema,
  sellerId: UlidSchema,
  sellerTenantId: TenantIdSchema,
  sellerName: z.string(),
  condominiumName: z.string(),
  title: z.string().min(3).max(80),
  description: z.string().max(1000).optional(),
  category: CategoriaListingSchema,
  condition: CondicionListingSchema,
  priceCLP: MontoClpSchema,
  photos: z.array(ListingPhotoSchema).min(1).max(5),
  status: EstadoListingSchema,
  aiGenerated: z.boolean().default(false),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});
export type Listing = z.infer<typeof ListingSchema>;

export const CreateListingRequestSchema = ListingSchema.pick({
  title: true,
  description: true,
  category: true,
  condition: true,
  priceCLP: true,
  aiGenerated: true,
}).extend({
  /** S3 keys de las fotos ya subidas via presigned URL */
  photoKeys: z.array(z.string()).min(1).max(5),
});
export type CreateListingRequest = z.infer<typeof CreateListingRequestSchema>;

export const UpdateListingRequestSchema = CreateListingRequestSchema.partial();
export type UpdateListingRequest = z.infer<typeof UpdateListingRequestSchema>;

export const ListingsResponseSchema = paginatedResponse(ListingSchema);
export type ListingsResponse = z.infer<typeof ListingsResponseSchema>;

/* ------------------------------------------------------------------ */
/*  AI Analysis                                                        */
/* ------------------------------------------------------------------ */

export const AiAnalysisResponseSchema = z.object({
  title: z.string(),
  description: z.string(),
  category: CategoriaListingSchema,
  condition: CondicionListingSchema.optional(),
  suggestedPriceCLP: MontoClpSchema.optional(),
  confidence: z.number().min(0).max(1),
});
export type AiAnalysisResponse = z.infer<typeof AiAnalysisResponseSchema>;

/* ------------------------------------------------------------------ */
/*  Seller Profile                                                     */
/* ------------------------------------------------------------------ */

export const SellerProfileSchema = z.object({
  sellerId: UlidSchema,
  tenantId: TenantIdSchema,
  displayName: z.string(),
  condominiumName: z.string(),
  memberSince: z.string(),
  ratingsPositive: z.number().int().nonnegative(),
  ratingsNeutral: z.number().int().nonnegative(),
  ratingsNegative: z.number().int().nonnegative(),
  itemsSold: z.number().int().nonnegative(),
  activeListingCount: z.number().int().nonnegative(),
});
export type SellerProfile = z.infer<typeof SellerProfileSchema>;

/* ------------------------------------------------------------------ */
/*  Seller Rating                                                      */
/* ------------------------------------------------------------------ */

export const SellerRatingSchema = z.object({
  id: UlidSchema,
  sellerId: UlidSchema,
  buyerId: UlidSchema,
  listingId: UlidSchema,
  rating: RatingValueSchema,
  comment: z.string().max(500).optional(),
  createdAt: IsoTimestampSchema,
});
export type SellerRating = z.infer<typeof SellerRatingSchema>;

export const CreateRatingRequestSchema = SellerRatingSchema.pick({
  listingId: true,
  rating: true,
  comment: true,
});
export type CreateRatingRequest = z.infer<typeof CreateRatingRequestSchema>;

/* ------------------------------------------------------------------ */
/*  Chat Thread                                                        */
/* ------------------------------------------------------------------ */

export const ChatThreadSchema = z.object({
  id: UlidSchema,
  listingId: UlidSchema,
  buyerId: UlidSchema,
  sellerId: UlidSchema,
  /** Denormalized for thread list display */
  listingTitle: z.string(),
  listingImageUrl: z.string().optional(),
  otherPartyName: z.string(),
  lastMessageAt: IsoTimestampSchema,
  lastMessagePreview: z.string(),
  unreadCount: z.number().int().nonnegative(),
  status: ChatThreadStatusSchema,
  createdAt: IsoTimestampSchema,
});
export type ChatThread = z.infer<typeof ChatThreadSchema>;

export const CreateThreadRequestSchema = z.object({
  listingId: UlidSchema,
  /** Initial message to start the conversation */
  message: z.string().min(1).max(2000),
});
export type CreateThreadRequest = z.infer<typeof CreateThreadRequestSchema>;

/* ------------------------------------------------------------------ */
/*  Chat Message                                                       */
/* ------------------------------------------------------------------ */

export const ChatMessageSchema = z.object({
  id: UlidSchema,
  threadId: UlidSchema,
  senderId: UlidSchema,
  senderName: z.string(),
  content: z.string().min(1).max(2000),
  createdAt: IsoTimestampSchema,
  readAt: IsoTimestampSchema.nullable(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const SendMessageRequestSchema = z.object({
  content: z.string().min(1).max(2000),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;
